// The client's single seconds→ticks derivation, shared by every canvas module that holds a
// presentation window as a wall-clock duration but ages it against a replay playhead.
//
// This exists as one exported symbol rather than a private copy per module because
// `tick-rate-single-source` made "one sanctioned derivation" a *rule* (`balance`, "One sanctioned
// derivation from seconds to ticks"), and a rule enforced by convention across duplicated private
// helpers is a rule waiting to drift: two copies that start identical diverge the first time one
// of them is "fixed". `CorpseTracker` (`corpse.ts`) and `DamageFlashTracker` (`damage-flash.ts`)
// both derive windows this way, so the helper belongs beside neither of them.

/**
 * `ms` converted to ticks at `tickRateHz` — **ceil, minimum 1**, mirroring `Balance::ticks_per`
 * (tick-rate-single-source): a derived window must never be *shorter* than the duration it names.
 *
 * Deliberately `Math.ceil`, not `Math.round`. The two agree at every tick count the canvas derives
 * at the shipped 15 Hz rate (`Math.round(22.5) === 23 === Math.ceil(22.5)` for a 1500 ms hold,
 * `Math.round(7.5) === 8 === Math.ceil(7.5)` for a 500 ms fade — JS's round-half-up ties happen to
 * already equal ceil for both products), so choosing `ceil` is inert at the shipped rate. They
 * diverge at other rates, where `round` can round *down* and yield a window shorter than the
 * wall-clock duration it names — the bug `ceil` exists to close.
 *
 * The minimum of 1 is what stops a window collapsing to zero ticks, which would silently disable
 * the presentation it bounds rather than fail.
 *
 * The result is quantised, so the contract every caller and test must read is a **bounded error,
 * not equality**: the effective duration is `>= ms` and `< ms` plus one tick period.
 */
export function ticksPer(ms: number, tickRateHz: number): number {
  return Math.max(1, Math.ceil((ms / 1000) * tickRateHz));
}
